import { base64ToBytes } from "./crypto.js";

export class VisualWatermark {
  constructor(container, payloadBytes, { label, onTamper } = {}) {
    this.container = container;
    this.onTamper = onTamper || (() => {});
    const bytes = payloadBytes || new Uint8Array(0);
    const tag = Array.from(bytes.slice(0, 4))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .toUpperCase();
    this.text = label || (tag ? `R-${tag}` : "RIGHTRA");
    this.el = null;
    this.timer = null;
    this.observer = null;
    this.detached = false;
  }

  attach() {
    this._build();
    this._move();
    this.timer = setInterval(() => this._move(), 7000);
    this.observer = new MutationObserver(() => this._check());
    this.observer.observe(this.container, {
      childList: true,
      subtree: true,
      attributes: true,
    });
  }

  _build() {
    const el = document.createElement("div");
    el.textContent = this.text;
    el.setAttribute("aria-hidden", "true");
    el.style.cssText = [
      "position:absolute",
      "z-index:2147483002",
      "pointer-events:none",
      "user-select:none",
      "font:600 clamp(11px,1.6vw,18px)/1 monospace",
      "letter-spacing:.08em",
      "color:#fff",
      "opacity:.12",
      "text-shadow:0 0 3px #000,0 0 1px #000",
    ].join(";");
    if (getComputedStyle(this.container).position === "static") {
      this.container.style.position = "relative";
    }
    this.container.appendChild(el);
    this.el = el;
  }

  _move() {
    if (!this.el) return;
    this.el.style.top = `${8 + Math.random() * 78}%`;
    this.el.style.left = `${4 + Math.random() * 80}%`;
  }

  _check() {
    if (this.detached) return;
    const gone = !this.el || !this.container.contains(this.el);
    let hidden = false;
    if (!gone) {
      const cs = getComputedStyle(this.el);
      hidden =
        cs.display === "none" ||
        cs.visibility === "hidden" ||
        parseFloat(cs.opacity) < 0.06 ||
        cs.pointerEvents !== "none";
    }
    if (gone || hidden) {
      this.onTamper(gone ? "watermark_removed" : "watermark_hidden");
      this.observer.disconnect();
      if (this.el && this.el.parentNode) this.el.parentNode.removeChild(this.el);
      this._build();
      this._move();
      this.observer.observe(this.container, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }
  }

  detach() {
    this.detached = true;
    clearInterval(this.timer);
    this.observer?.disconnect();
    if (this.el && this.el.parentNode) this.el.parentNode.removeChild(this.el);
    this.el = null;
  }
}

export function applyWatermarkToCanvas(canvas, payloadBytes, frameWidth, frameHeight) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const seed = payloadBytes.length
    ? payloadBytes[0] * 256 + payloadBytes[1 % payloadBytes.length]
    : 0;
  const rand = mulberry32(seed);
  const dotCount = Math.max(32, Math.floor((frameWidth * frameHeight) / 250000));

  ctx.save();
  ctx.globalAlpha = 0.03;
  ctx.fillStyle = "#ffffff";
  for (let i = 0; i < dotCount; i++) {
    ctx.fillRect(Math.floor(rand() * frameWidth), Math.floor(rand() * frameHeight), 1, 1);
  }
  ctx.restore();
}

export function decodeWatermarkPayload(base64Payload) {
  try {
    return base64ToBytes(base64Payload);
  } catch {
    return new Uint8Array(0);
  }
}

function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}